import React, {useState} from 'react';
import {
  CourseAnalyticsLearnerDetails,
  CourseAnalyticsLearnerDetailsLesson,
} from '../../../../../data/models/Gradebook/CourseAnalyticsModels';
import {getFirstCapitals} from '../../../../../utils/getFirstCapitals';
import {analyticsText} from '../../../analyticsText';
import {
  getTimeTextFromSeconds,
  truncateNumberTo2Decimals,
} from '../../../../../utils/getTimeTextFromSeconds';

interface Props {
  analytics: CourseAnalyticsLearnerDetails;
  onClose: () => void;
  onOpenLearningJourney: (learnerId: string, lessonId: string) => void;
  isGradable: boolean;
  onChangeGrade: (
    learnerId: string,
    lessonId: string,
    newGrade: number,
  ) => Promise<boolean>;
}

const LearnerAnalyticsDetails = React.memo(
  ({
    analytics,
    onClose,
    onOpenLearningJourney,
    isGradable,
    onChangeGrade,
  }: Props) => {
    // Edit score state
    const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [localLessons, setLocalLessons] = useState<
      CourseAnalyticsLearnerDetailsLesson[]
    >(analytics.lessons);

    // Parent/child accordion logic
    const {parents, childrenMap, orphanLessons} = React.useMemo(() => {
      const parentList: CourseAnalyticsLearnerDetailsLesson[] = [];
      const parentIds = new Set<string>();
      localLessons.forEach(lesson => {
        if (lesson.relatedPublishedLessonId == null) {
          parentList.push(lesson);
          parentIds.add(lesson.id);
        }
      });
      const childMap: Record<string, CourseAnalyticsLearnerDetailsLesson[]> =
        {};
      const orphans: CourseAnalyticsLearnerDetailsLesson[] = [];
      localLessons.forEach(lesson => {
        if (lesson.relatedPublishedLessonId != null) {
          if (parentIds.has(lesson.relatedPublishedLessonId)) {
            if (!childMap[lesson.relatedPublishedLessonId]) {
              childMap[lesson.relatedPublishedLessonId] = [];
            }
            childMap[lesson.relatedPublishedLessonId].push(lesson);
          } else {
            orphans.push(lesson);
          }
        }
      });
      return {
        parents: parentList,
        childrenMap: childMap,
        orphanLessons: orphans,
      };
    }, [localLessons]);

    const [expandedParents, setExpandedParents] = React.useState<
      Record<string, boolean>
    >({});

    React.useEffect(() => {
      const nextExpanded: Record<string, boolean> = {};
      parents.forEach(parent => {
        if (childrenMap[parent.id]?.length) {
          nextExpanded[parent.id] = true;
        }
      });
      setExpandedParents(nextExpanded);
    }, [parents, childrenMap]);

    const toggleParent = React.useCallback((lessonId: string) => {
      setExpandedParents(prev => ({
        ...prev,
        [lessonId]: !prev[lessonId],
      }));
    }, []);

    // Edit score handlers
    // const handleEditClick = (
    //   e: React.MouseEvent,
    //   lessonId: string,
    //   currentScore: number,
    // ) => {
    //   e.stopPropagation();
    //   setEditingLessonId(lessonId);
    //   setEditValue(currentScore.toString());
    // };

    const handleCancelEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingLessonId(null);
      setEditValue('');
    };

    const handleSaveGrade = async (e: React.MouseEvent, lessonId: string) => {
      e.stopPropagation();
      const newGrade = parseFloat(editValue);
      if (isNaN(newGrade) || newGrade < 0 || newGrade > 100) {
        return;
      }
      setIsSaving(true);
      const success = await onChangeGrade(
        analytics.learnerId,
        lessonId,
        newGrade,
      );
      if (success) {
        setLocalLessons(prev =>
          prev.map(lesson =>
            lesson.id === lessonId ? {...lesson, score: newGrade} : lesson,
          ),
        );
        setEditingLessonId(null);
        setEditValue('');
      }
      setIsSaving(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '') {
        setEditValue('');
        return;
      }
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        setEditValue(value);
      }
    };

    const handleInputClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    const renderLessonRow = (
      lesson: CourseAnalyticsLearnerDetailsLesson,
      isChild: boolean,
      hasChildren: boolean,
      isExpanded: boolean,
    ) => (
      <tr
        key={lesson.id}
        onClick={() => onOpenLearningJourney(analytics.learnerId, lesson.id)}>
        <td>
          <div className={`LessonNameCell${isChild ? ' isChild' : ''}`}>
            <span className="LessonNameText">{lesson.name}</span>
            {hasChildren ? (
              <button
                className="LessonAccordionToggle"
                onClick={event => {
                  event.stopPropagation();
                  toggleParent(lesson.id);
                }}
                aria-expanded={isExpanded}
                aria-label={analyticsText.lesson}>
                <span
                  className={`LessonAccordionCaret${
                    isExpanded ? ' isExpanded' : ''
                  }`}
                />
              </button>
            ) : null}
          </div>
        </td>
        <td>
          {lesson.status === 0
            ? analyticsText.completed
            : lesson.status === 1
            ? analyticsText.inProgress
            : analyticsText.notStarted}
        </td>
        <td>
          {lesson.duration !== null && lesson.duration !== undefined
            ? getTimeTextFromSeconds(lesson.duration)
            : '-'}
        </td>
        {isGradable && (
          <td className="ScoreCell">
            {editingLessonId === lesson.id ? (
              <div className="ScoreEditContainer">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={editValue}
                  onChange={handleInputChange}
                  onClick={handleInputClick}
                  className="ScoreEditInput"
                  disabled={isSaving}
                />
                <button
                  className="ScoreEditButton SaveButton"
                  onClick={e => handleSaveGrade(e, lesson.id)}
                  disabled={isSaving}>
                  {analyticsText.save}
                </button>
                <button
                  className="ScoreEditButton CancelButton"
                  onClick={handleCancelEdit}
                  disabled={isSaving}>
                  {analyticsText.cancel}
                </button>
              </div>
            ) : (
              <div className="ScoreDisplayContainer">
                <span>
                  {lesson.score !== null && lesson.score !== undefined
                    ? truncateNumberTo2Decimals(lesson.score) + '%'
                    : '-'}
                </span>
                {/* {lesson.score !== null && lesson.score !== undefined && (
                  <button
                    className="EditScoreButton"
                    onClick={e => handleEditClick(e, lesson.id, lesson.score!)}>
                    {analyticsText.edit}
                  </button>
                )} */}
              </div>
            )}
          </td>
        )}
      </tr>
    );

    return (
      <div className="LearnerAnalyticsDetailsContainer">
        <button className="CloseButton" onClick={onClose}>
          <span className="icon-x-close"></span>
        </button>
        <div className="AvatarContainer">
          {analytics.avatarUrl ? (
            <img src={analytics.avatarUrl} />
          ) : (
            <div className="AvatarPlaceholder">
              {getFirstCapitals(analytics.name)}
            </div>
          )}
          <span className="LearnerName">{analytics.name}</span>
        </div>
        <div className="QuickStatsContainer">
          <div className="QuickStat">
            <p>{analytics.passedLessons}</p>
            <span>{analyticsText.passed}</span>
          </div>
          <div className="QuickStat">
            <p>{analytics.failedLessons}</p>
            <span>{analyticsText.failed}</span>
          </div>
          {/* <div className="QuickStat">
            <p>
              {analytics.avgScore
                ? truncateNumberTo2Decimals(analytics.avgScore)
                : 0}
              {analytics.avgScore ? (
                <span className="QuckStatPercent">%</span>
              ) : null}
            </p>
            <span>{analyticsText.avgScore}</span>
          </div> */}
        </div>
        <div className="LearnersTableContainer">
          <table>
            <thead>
              <tr>
                <th>{analyticsText.lesson}</th>
                <th>{analyticsText.status}</th>
                <th>{analyticsText.duration}</th>
                {isGradable && <th>{analyticsText.score}</th>}
              </tr>
            </thead>
            <tbody>
              {[...parents, ...orphanLessons].map(parentLesson => {
                const children = childrenMap[parentLesson.id] || [];
                const hasChildren = children.length > 0;
                const isExpanded = expandedParents[parentLesson.id] ?? false;
                return (
                  <React.Fragment key={parentLesson.id}>
                    {renderLessonRow(
                      parentLesson,
                      false,
                      hasChildren,
                      isExpanded,
                    )}
                    {hasChildren && isExpanded
                      ? children.map(childLesson =>
                          renderLessonRow(childLesson, true, false, false),
                        )
                      : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);

export default LearnerAnalyticsDetails;
